import backendOld from "~/api/backendOld";
import { getAgentConfigDisplayName } from "~/constants/agents/configs";
import EXECUTION_MODE_DISPLAY from "~/constants/agents/executionMode";
import { USER_KIND_CUSTOM } from "~/constants/userKinds";
import { ExecutionMode, TutorialName, UserStatus } from "~/shared/enums";
import type { AgentConfig, AgentExtension, User } from "~/shared/types";
import { useAppStore, useDataStore, useUserStore } from "~/stores";
import { deepCopy, getNextTitleInSequence, makeDuid } from "~/utils/common";
import { getActiveDescriptionText, makeDefaultAgentExtension } from "~/utils/user";

const openAgentPage = (duid: string): Promise<void> =>
  useDataStore().$navigateToPendingDuid(duid, { name: "agent", params: { agentDuid: duid } });

const getExecutionModeAgentName = (executionMode: ExecutionMode) =>
  executionMode === ExecutionMode.LOCAL
    ? useUserStore().localSetupAgentName
    : `${EXECUTION_MODE_DISPLAY[executionMode].label} agent`;

const createAndOpenAgent = async (
  config?: AgentConfig,
  sourceAgent?: User,
  name?: string,
  options: { executionMode?: ExecutionMode; navigate?: boolean } = {}
): Promise<User> => {
  const appStore = useAppStore();
  const dataStore = useDataStore();
  const { executionMode, navigate = true } = options;

  const existingNames = dataStore
    .getUserList({ excludeNonagents: true, includeInactiveAgents: true })
    .map((e) => e.name);

  const isReplicating = !!sourceAgent && sourceAgent.kind === (config?.name ?? USER_KIND_CUSTOM);
  const agentExtension: AgentExtension | null =
    isReplicating && sourceAgent?.agent
      ? { ...sourceAgent.agent, webhookDuid: null }
      : executionMode
        ? makeDefaultAgentExtension({ executionMode })
        : null;
  const baseName =
    name ??
    (isReplicating && sourceAgent
      ? sourceAgent.name
      : config
        ? getAgentConfigDisplayName(config)
        : executionMode
          ? getExecutionModeAgentName(executionMode)
          : "Agent");
  const hasWebhookField = !!config?.configFields.some((field) => field.type === "webhook");
  const duid = makeDuid();
  if (navigate) {
    dataStore.$addPendingNavigationDuid(duid);
  }
  const agentPromise = dataStore.createAgent(
    getNextTitleInSequence(baseName, existingNames, { noCopy: true }),
    {
      ...(config && { kind: config.name }),
      ...(isReplicating && sourceAgent
        ? {
            colorHex: sourceAgent.colorHex,
            manualDescription: getActiveDescriptionText(sourceAgent),
            usingGeneratedDescription: false,
            aiGuidelines: sourceAgent.aiGuidelines ?? "",
          }
        : config && {
            colorHex: config.color,
            manualDescription: config.description,
            usingGeneratedDescription: false,
          }),
      status: isReplicating && sourceAgent ? sourceAgent.status : UserStatus.AGENT_CONFIG_ERROR,
      ...(agentExtension && { agent: agentExtension }),
    },
    { awaitBackend: false, duid }
  );

  if (!config && !isReplicating) {
    appStore.pendingAgentImageGenerationDuid = duid;
  }
  const navigationPromise = navigate ? openAgentPage(duid) : undefined;

  const agent = await agentPromise;

  if (isReplicating && agentExtension) {
    await dataStore.updateAgent(
      {
        duid: agent.duid,
        agent: {
          ...deepCopy(agentExtension),
          webhookDuid: null,
        },
      },
      { awaitBackend: true }
    );
  }
  if (hasWebhookField && agent.agent) {
    const { data } = await backendOld.agents.rotateWebhook(agent.duid);
    agent.agent.webhookDuid = data.webhookDuid;
  }
  await navigationPromise;
  return agent;
};

export const createNewAgent = async (): Promise<void> => {
  const appStore = useAppStore();
  const userStore = useUserStore();
  if (userStore.getTutorialStatus(TutorialName.SET_UP_AGENT) === 1) {
    userStore.updateTutorialStatuses([{ name: TutorialName.SET_UP_AGENT, status: 2 }]);
  }
  appStore.setAgentCreationModal({ pendingSetupDuid: null });
};

export default createAndOpenAgent;
